// Bans — keeping a player out of one venue, of every venue of an organisation, or of the Platform
// (spec/07 §3.5, F-IAM-07).
//
//   POST /v1/organizations/{o}/venues/{v}/players/{p}/ban      players:ban, at a venue the member works at
//   POST /v1/organizations/{o}/venues/{v}/players/{p}/unban    players:ban, likewise
//   POST /v1/organizations/{o}/players/{p}/ban                 players:ban, a member who works at every venue
//   POST /v1/organizations/{o}/players/{p}/unban               players:ban, likewise
//   GET  /v1/organizations/{o}/players/{p}/bans                players:ban
//   POST /v1/hq/players/{p}/ban                                hq:players
//   POST /v1/hq/players/{p}/unban                              hq:players
//
// **What each scope stops.** A venue ban stops the player starting a session or ordering at that
// venue; an organisation ban, at every venue of the organisation; a platform ban stops them signing
// in anywhere, and revokes every session they hold. The server enforces every one of them — at the
// kiosk's sign-in today, and at session start, checkout, reservation, realtime connect and token
// refresh as each of those lands — never a client hiding a button.
//
// **A ban's reason is internal.** It comes back to staff holding `players:ban` from these routes and
// from nowhere else: the kiosk is told `player-banned` and shows "Please see the front desk", the
// same words for every scope, and no event, realtime frame or problem document carries it. One
// organisation never sees another's bans, nor why HQ banned someone.
//
// **One ban per scope.** Banning a player again where a ban is already on record — in force or
// lapsed — changes that ban's reason and expiry (`200`) rather than adding a second (`201`). Lifting
// one needs a reason too. A lifted ban stays on record; nothing here deletes one. Every ban, change
// and lift writes one audit row with the actor, the player, the reason and the request id
// (spec/07 §11).
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { OrganizationIdSchema, PlayerBanIdSchema, PlayerIdSchema, VenueIdSchema } from "../ids/schema.js";
import { commonProblemResponses, jsonResponse, problemResponse } from "../openapi/responses.js";
import { HQ_PLAYERS, PLAYERS_BAN } from "../permissions/index.js";
import { PageQuerySchema, paginated } from "../primitives/page.js";
import { IsoDateTimeSchema } from "../primitives/scalars.js";
import { OrganizationScopeParamsSchema, VenueScopeParamsSchema } from "../primitives/scope.js";
import { PERMISSION_EXTENSION } from "./audit.js";
/** The longest reason a ban, a change to one or its lifting may give. */
export const BAN_REASON_MAX_LENGTH = 500;
/**
 * What the kiosk is told about a banned player, whatever the scope (spec/05 §3.2 check 3, spec/12):
 * the problem document's `detail`, word for word. Never the reason, the scope or the expiry.
 */
export const PLAYER_BANNED_DETAIL = 'Please see the front desk.';
export const BanReasonSchema = z
    .string()
    .trim()
    .min(1)
    .max(BAN_REASON_MAX_LENGTH)
    .openapi('BanReason', {
    description: 'Why, for the staff and for the audit log. Internal: never shown to the player or on a kiosk.',
});
export const BanPlayerSchema = z
    .object({
    reason: BanReasonSchema,
    /**
     * When the ban stops applying, in the future. Absent or `null`: until it is lifted. Sent to a ban
     * already on record, it replaces that ban's expiry.
     */
    expiresAt: IsoDateTimeSchema.nullable().optional(),
})
    .openapi('BanPlayer');
export const LiftBanSchema = z.object({ reason: BanReasonSchema }).openapi('LiftBan');
export const PLAYER_BAN_SCOPES = ['venue', 'organization'];
export const PlayerBanScopeSchema = z.enum(PLAYER_BAN_SCOPES).openapi('PlayerBanScope');
/**
 * `active` stops the player now; `expired` passed its `expiresAt` and stops nothing, though it stays
 * the ban on record at its scope until it is changed or lifted; `lifted` was lifted by staff.
 */
export const PLAYER_BAN_STATUSES = ['active', 'expired', 'lifted'];
export const PlayerBanStatusSchema = z.enum(PLAYER_BAN_STATUSES).openapi('PlayerBanStatus');
export const PlayerBanSchema = z
    .object({
    id: PlayerBanIdSchema,
    organizationId: OrganizationIdSchema,
    scope: PlayerBanScopeSchema,
    /** The venue, for a venue ban; `null` for an organisation ban. */
    venueId: VenueIdSchema.nullable(),
    playerId: PlayerIdSchema,
    /** Internal (spec/07 §3.5). Only a holder of `players:ban` is ever sent it. */
    reason: z.string(),
    expiresAt: IsoDateTimeSchema.nullable(),
    status: PlayerBanStatusSchema,
    bannedAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
    liftedAt: IsoDateTimeSchema.nullable(),
    /** Why it was lifted. Internal, like `reason`. */
    liftReason: z.string().nullable(),
})
    .openapi('PlayerBan', { description: 'A player’s ban at one venue or across one organisation (spec/07 §3.5).' });
export const PlayerBanListSchema = paginated(PlayerBanSchema, 'PlayerBanList');
export const PlatformBanSchema = z
    .object({
    playerId: PlayerIdSchema,
    /** Whether a platform ban stops this person signing in now. */
    banned: z.boolean(),
    /** Internal, HQ's alone. `null` when there is no ban on record. */
    reason: z.string().nullable(),
    expiresAt: IsoDateTimeSchema.nullable(),
})
    .openapi('PlatformBan', { description: 'Whether a person is banned from the Platform (spec/07 §3.5).' });
const OrganizationPlayerParamsSchema = OrganizationScopeParamsSchema.extend({ playerId: PlayerIdSchema }).openapi('OrganizationPlayerParams');
const VenuePlayerParamsSchema = VenueScopeParamsSchema.extend({ playerId: PlayerIdSchema }).openapi('VenuePlayerParams');
export const PlatformPlayerParamsSchema = z.object({ playerId: PlayerIdSchema }).openapi('PlatformPlayerParams');
const json = (schema) => ({
    body: { content: { 'application/json': { schema } }, required: true },
});
const ONE_PER_SCOPE = 'A ban already on record at this scope — in force or lapsed — is changed to this reason and expiry ' +
    '(`200`) rather than joined by a second (`201`). One audit row either way.';
const noSuchPlayer = problemResponse('No such organisation, venue or player, or not one you can see.', 'not-found');
const noBanHere = problemResponse('No such organisation, venue or player, or no ban on record for them at this scope.', 'not-found');
const raced = problemResponse('Someone changed this player’s ban at the same moment; send it again.', 'conflict');
export const banPlayerAtVenueRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/players/{playerId}/ban',
    operationId: 'banPlayerAtVenue',
    tags: ['players'],
    summary: 'Ban a player from one venue',
    description: 'The player can no longer start a session or order at this venue; every other venue is untouched. ' +
        `Needs \`players:ban\` and a venue scope that includes this venue. ${ONE_PER_SCOPE}`,
    [PERMISSION_EXTENSION]: PLAYERS_BAN,
    request: { params: VenuePlayerParamsSchema, ...json(BanPlayerSchema) },
    responses: {
        200: jsonResponse(PlayerBanSchema, 'The ban on record here, changed.'),
        201: jsonResponse(PlayerBanSchema, 'The new ban.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: noSuchPlayer,
        409: raced,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const unbanPlayerAtVenueRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/players/{playerId}/unban',
    operationId: 'unbanPlayerAtVenue',
    tags: ['players'],
    summary: 'Lift a player’s ban at one venue',
    description: 'Lifts the ban on record at this venue, with a reason; it stays on record as lifted. An organisation ' +
        'ban, if there is one, still applies here. Needs `players:ban` and a venue scope that includes this venue.',
    [PERMISSION_EXTENSION]: PLAYERS_BAN,
    request: { params: VenuePlayerParamsSchema, ...json(LiftBanSchema) },
    responses: {
        200: jsonResponse(PlayerBanSchema, 'The ban, lifted.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: noBanHere,
        409: raced,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const banPlayerRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/players/{playerId}/ban',
    operationId: 'banPlayer',
    tags: ['players'],
    summary: 'Ban a player from every venue of the organisation',
    description: 'The player can no longer start a session or order at any venue of this organisation, including ' +
        'venues opened later. Needs `players:ban` and a member who works at every venue — an owner, or a ' +
        'member with no venue scope. Names no venue: `X-Clavian-Venue` is refused here; to ban at one venue, ' +
        `use \`…/venues/{venueId}/players/{playerId}/ban\`. ${ONE_PER_SCOPE}`,
    [PERMISSION_EXTENSION]: PLAYERS_BAN,
    request: { params: OrganizationPlayerParamsSchema, ...json(BanPlayerSchema) },
    responses: {
        200: jsonResponse(PlayerBanSchema, 'The organisation ban on record, changed.'),
        201: jsonResponse(PlayerBanSchema, 'The new organisation ban.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: noSuchPlayer,
        409: raced,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const unbanPlayerRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/players/{playerId}/unban',
    operationId: 'unbanPlayer',
    tags: ['players'],
    summary: 'Lift a player’s organisation ban',
    description: 'Lifts the organisation ban on record, with a reason; it stays on record as lifted. A ban at one ' +
        'venue, if there is one, still applies there. Needs `players:ban` and a member who works at every venue.',
    [PERMISSION_EXTENSION]: PLAYERS_BAN,
    request: { params: OrganizationPlayerParamsSchema, ...json(LiftBanSchema) },
    responses: {
        200: jsonResponse(PlayerBanSchema, 'The organisation ban, lifted.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: noBanHere,
        409: raced,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const listPlayerBansRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/players/{playerId}/bans',
    operationId: 'listPlayerBans',
    tags: ['players'],
    summary: 'A player’s bans in this organisation, reasons included',
    description: 'Newest first: in force, lapsed and lifted, at every venue the member works at and across the ' +
        'organisation. This organisation’s bans only — never another’s, and never a platform ban. Needs `players:ban`.',
    [PERMISSION_EXTENSION]: PLAYERS_BAN,
    request: { params: OrganizationPlayerParamsSchema, query: PageQuerySchema },
    responses: {
        200: jsonResponse(PlayerBanListSchema, 'A page of bans.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: noSuchPlayer,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
const platformForbidden = problemResponse('Not HQ, no `hq:players`, no second factor on the session, or banning yourself.', 'forbidden', 'second-factor-required');
export const banPlayerOnPlatformRoute = createRoute({
    method: 'post',
    path: '/v1/hq/players/{playerId}/ban',
    operationId: 'banPlayerOnPlatform',
    tags: ['hq'],
    summary: 'Ban a person from the Platform',
    description: 'They cannot sign in anywhere — web, apps or kiosk — and every session they hold is revoked at once; ' +
        'their open realtime sockets are told `access.revoked`. Sent to someone already banned, it changes the ' +
        'reason and expiry. Platform scope: requires `hq:players` and a second factor. One platform-level audit row.',
    [PERMISSION_EXTENSION]: HQ_PLAYERS,
    request: { params: PlatformPlayerParamsSchema, ...json(BanPlayerSchema) },
    responses: {
        200: jsonResponse(PlatformBanSchema, 'The platform ban as it now stands.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: platformForbidden,
        404: problemResponse('No such player.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const unbanPlayerOnPlatformRoute = createRoute({
    method: 'post',
    path: '/v1/hq/players/{playerId}/unban',
    operationId: 'unbanPlayerOnPlatform',
    tags: ['hq'],
    summary: 'Lift a person’s platform ban',
    description: 'They can sign in again; sessions revoked by the ban stay revoked. Platform scope: requires `hq:players` ' +
        'and a second factor. One platform-level audit row.',
    [PERMISSION_EXTENSION]: HQ_PLAYERS,
    request: { params: PlatformPlayerParamsSchema, ...json(LiftBanSchema) },
    responses: {
        200: jsonResponse(PlatformBanSchema, 'The platform ban as it now stands: none.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: platformForbidden,
        404: problemResponse('No such player, or no platform ban on record for them.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const PLAYER_BAN_ROUTES = [
    banPlayerAtVenueRoute,
    unbanPlayerAtVenueRoute,
    banPlayerRoute,
    unbanPlayerRoute,
    listPlayerBansRoute,
    banPlayerOnPlatformRoute,
    unbanPlayerOnPlatformRoute,
];
//# sourceMappingURL=player-bans.js.map